/**
 * Note: Do not use this non-form component in your form. Use the core/form/components/Checkbox instead.
 */
import React from 'react';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { display, space, width } from 'styled-system';
import { Checkbox as BlueprintCheckbox, Classes } from '@blueprintjs/core';
import { CheckboxProps, StyledCheckboxProps } from 'core/components/types';
import { formAriaProps, guessByChildText } from '../util/ariaUtils';

const StyledCheckbox = styled(BlueprintCheckbox)<StyledCheckboxProps>`
  &.${Classes.CONTROL} {
    ${space};
    ${width};
    ${display}
  }

  // the indeterminate state is currently very hard to distinguish from the checked state
  // in large lists, so we're graying it out to make it more obvious
  &.bp4-control.bp4-checkbox input:indeterminate ~ .bp4-control-indicator {
    background-image: none;
    filter: grayscale(1);
  }

  /* remove 6px right margin when there's no label */
  ${(props) =>
    !props.labelElement &&
    !props.label &&
    css`
      padding-left: 16px;

      .bp4-control-indicator {
        margin-left: -16px;
        margin-right: 0;
      }
    `}
`;

const Checkbox: {
  (props: CheckboxProps): JSX.Element;
  displayName?: string;
} = observer((props) => {
  // TODO move this into function declaration, can't do it right now since it messes up rename operation
  const {
    m = 0,
    mb = '2px',
    onChange = () => {},
    alignItems: _alignItems = 'center',
    display: _display = 'flex',
    ariaLabel,
    className,
    small,
    readOnly,
    style,
    ...rest
  } = props;
  const handleChange = (e: React.MouseEvent<HTMLInputElement>) => {
    if (!readOnly) {
      onChange(e.currentTarget.checked);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // note space us handled by blueprint
    if (e.key === 'Enter') {
      if (!readOnly) {
        onChange(e.currentTarget.checked);
      }
    }
  };

  const ariaLabelGuess = guessByChildText(rest.labelElement) || rest?.label;

  const ariaProps = formAriaProps(
    rest,
    { ariaLabel: ariaLabel || ariaLabelGuess, ariaSelected: readOnly },
    { enableTabIndexNotDisabled: true, passOnExistingAria: true }
  );

  return (
    <StyledCheckbox
      {...ariaProps}
      {...rest}
      m={m}
      mb={mb}
      display={_display}
      onKeyDown={handleKeyDown}
      className={classNames(className, { [Classes.SMALL]: small })}
      onChange={handleChange}
      // Can't set alignItems directly on StyledCheckbox as React complains
      style={{ alignItems: _alignItems, ...style }}
    />
  );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;
